/* eslint-disable sonarjs/cognitive-complexity */
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { MODALS } from '../brands/default/constants';
import {
  PROGRESS_MESSAGE_SUCCESS,
  VIP_PAYWALL,
} from '../brands/default/content/components/vipPaywall';
import { VIP_PAYWALL_LOGO } from '../brands/default/features';
import { LINK_HOME } from '../brands/default/links';
import { VIP_LOGO, WOW } from '../config/constants/images';
import { trackEvent, updateDod } from '../helpers/analytics';
import expressBuyService from '../helpers/expressBuyService';
import { isMobileViewport, useScreenBreakpoint } from '../helpers/screen';
import { usePaymentInfo } from '../helpers/secondCheckout';
import { redirectToURL } from '../helpers/url';
import { getVipDeal } from '../helpers/vipSubscribeHelper';
import ThemeContext from '../providers/ThemeProvider';
import { getProfile } from '../redux/actions/user';
import VipDetailsTable from './VipDetailsTable';
import VipLoginModal from './VipLoginModal';
import WowModal from './WowModal';
import PrimaryButton from './_generic/button/PrimaryButton';
import Spinner from './_generic/spinner/Spinner';
import useToast from './_generic/toast/UseToast';
import LoginDetails from './checkout/LoginDetails';
import PaymentVipModal from './checkout/payment/PaymentVipModal';

const VipPaywall = ({
  ariaLabelledBy,
  backdrop,
  backdropClassName,
  container,
  dialogId,
  extraClasses,
  keyboard = true, // esc will work by default
  size,
  topPosition,
  showModal,
}) => {
  const theme = useContext(ThemeContext);

  const ref = useRef(null);
  const router = useRouter();

  const [isAuthenticated, isAuthCheckFinished] = useSelector((state) => [
    state.user.isAuthenticated,
    state.user.isAuthCheckFinished,
  ]);
  const toast = useToast();
  const dispatch = useDispatch();
  const [paymentInstruments, setPaymentInstruments] = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [failedExpressBuy, setFailedExpressBuy] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [enablePaymentModule, setEnablePaymentModule] = useState(false);
  const [modalBackdrop, setModalBackdrop] = useState('static'); // can't close click outside
  const [progressMessage, setProgressMessage] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [userCanCloseModal, setUserCanCloseModal] = useState(false);
  const [showLoginModule, setShowLoginModule] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [vipDealData, setVipDealData] = useState({});
  const [showPaywall, setShowPaywall] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [canBuyNow, setCanBuyNow] = useState(false);
  const userState = useSelector((state) => state.user.isAuthenticated);

  const paymentInfo = usePaymentInfo(userState);

  const breakpoint = useScreenBreakpoint();

  useEffect(() => {
    setIsMobile(isMobileViewport());
  }, [breakpoint]);

  useEffect(() => {
    if (paymentInfo && paymentInfo.paymentInstruments) {
      setPaymentInstruments(paymentInfo.paymentInstruments);
      setDeviceData(paymentInfo.paymentDeviceProfileVO.sessionId);
    }
    if (paymentInfo && paymentInfo.buyNow) {
      setCanBuyNow(paymentInfo.buyNow);
    }
  }, [paymentInfo]);
  useEffect(() => {
    if (enablePaymentModule && userState) {
      setTimeout(() => {
        setShowPaymentModal(true);
      }, 2_000);
    }
  }, [enablePaymentModule, userState]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      const getVipDealData = async () => {
        const vipDealObject = await getVipDeal();
        const response = vipDealObject?.data;
        if (response) setVipDealData(response);
      };
      getVipDealData();
    }

    return () => {
      mounted = false;
    }; // cleanup function
  }, []);
  const setModalId = (dialogElement) => {
    dialogElement.setAttribute('id', dialogId);
  };

  const expressBuyFailToCheckout = () => {
    let timeout = 5_000;
    // no messages on express buy failure, open payment modal quickly
    timeout = 500;

    setModalBackdrop(true); // can click bg to close
    setShowSpinner(false);
    setShowMessageModal(false);
    setUserCanCloseModal(true);

    setTimeout(() => {
      // close message after 5 seconds and show payment modal
      // if (addonExpressBuyService.failedPayment) setFailedExpressBuy(true);
      setShowPaymentModal(true);
    }, timeout);
  };

  const expressBuyMessage = (message) => {
    setProgressMessage(message);
  };

  const expressBuySuccess = (isPaymentModal = false) => {
    setShowSpinner(false);
    setShowMessageModal(false);
    setProgressMessage(PROGRESS_MESSAGE_SUCCESS);
    setShowPaywall(false);
    setUserCanCloseModal(true);
    if (isPaymentModal) {
      setShowPaymentModal(false);
    }
    dispatch(getProfile());
    getProfile();
    updateDod({
      dealId: vipDealData?.id,
      orderValue: vipDealData?.price,
      Page_name: 'paywall',
    });
    trackEvent('viphub: paywall buy');
    trackEvent('viphub: paywall rev');
    toast.addToast(VIP_PAYWALL.paywallSuccessMessage, 'toast-success');
  };

  const handleButtonClick = () => {
    const deal = {
      boughtToday: false,
      currency: vipDealData?.currency,
      eligible: true,
      excludedByUser: false,
      id: vipDealData?.id,
      originalPrice: vipDealData?.originalPrice,
      pageTitle: vipDealData?.pageTitle,
      price: vipDealData?.price,
      productId: vipDealData?.products[0]?.id,
      products: vipDealData?.products,
      status: vipDealData?.status,
      title: vipDealData?.title,
    };
    if (!isAuthenticated && isAuthCheckFinished) {
      // Display the Login Module
      setShowLoginModule(true);
      setShowPaywall(false);
    } else if (isAuthenticated && isAuthCheckFinished && canBuyNow) {
      // Continue quick buy
      setModalBackdrop('static'); // can't click bg to close
      setShowSpinner(true);
      setShowMessageModal(true);
      expressBuyService.failToCheckout = expressBuyFailToCheckout;
      expressBuyService.success = expressBuySuccess;
      expressBuyService.setProgressTest = (message) =>
        expressBuyMessage(message);
      expressBuyService.initExpressBuy(deal, 1);
    } else {
      setFailedExpressBuy(false);
      setShowPaymentModal(true);
      setShowPaywall(false);
    }
  };

  const handlePaywallClose = () => {
    trackEvent('viphub: paywallout click');
    redirectToURL(LINK_HOME, router);
  };
  if (!showPaywall && !showPaymentModal && !showLoginModule && !showModal)
    return null;

  return (
    <>
      {showPaywall && (
        <Modal
          aria-labelledby={ariaLabelledBy}
          backdrop={backdrop}
          backdropClassName={backdropClassName}
          centered
          className={extraClasses}
          container={container}
          keyboard={keyboard}
          onEntered={setModalId}
          show={showModal}
          size={size}
        >
          <Modal.Body style={{ backgroundColor: '#000000' }}>
            <div className="paywall-content">
              <div className="paywall-header">
                <p
                  dangerouslySetInnerHTML={{
                    __html: VIP_PAYWALL.paywallHeader,
                  }}
                />
              </div>
              <div className="paywall-content-subtitle">
                {isMobile ? (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: VIP_PAYWALL.paywallSubtitleMobile,
                    }}
                  />
                ) : (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: VIP_PAYWALL.paywallSubtitle,
                    }}
                  />
                )}
              </div>
              <div className="paywall-content-table">
                <VipDetailsTable />
              </div>
              <div className="paywall-content-divider">
                {VIP_PAYWALL_LOGO && (
                  <img
                    alt="Logo"
                    className="paywall-content-divider__img"
                    height={isMobile ? '50px' : `80px`}
                    src={WOW}
                  />
                )}
                <div className="paywall-content-divider-text">
                  {VIP_PAYWALL.paywallDividerText}
                </div>
                {VIP_PAYWALL_LOGO && (
                  <img
                    alt="Logo"
                    className="paywall-content-divider__img"
                    height={isMobile ? '50px' : `80px`}
                    src={WOW}
                  />
                )}
              </div>
              <div className="paywall-content-savings-button">
                <PrimaryButton
                  ariaLabel={VIP_PAYWALL.paywallSavingsButton}
                  className="savings-button"
                  onClick={() => {
                    trackEvent('viphub: paywallin click');
                    handleButtonClick();
                  }}
                >
                  {VIP_PAYWALL.paywallSavingsButton}
                </PrimaryButton>
              </div>
              <div className="paywall-content-underline-text">
                <button
                  className="link-button"
                  onClick={handlePaywallClose}
                  type="button"
                >
                  {VIP_PAYWALL.paywallHomeLink}
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
      {showPaymentModal && (
        <PaymentVipModal
          currentRef={ref.current}
          deviceData={deviceData}
          failedExpressBuy={failedExpressBuy}
          isAuthCheckFinished
          isAuthenticated
          orderCompleteHandler={expressBuySuccess}
          orderFailedHandler={expressBuyFailToCheckout}
          paymentInstruments={paymentInstruments}
          setShowPaymentModal={setShowPaymentModal}
          setShowPaywall={setShowPaywall}
          showPaymentModal={showPaymentModal}
          vipDealData={vipDealData}
        />
      )}
      <WowModal
        ariaLabelledBy="payment-progress"
        backdrop={modalBackdrop}
        centered
        setShowModal={setShowMessageModal}
        showCloseButton={userCanCloseModal}
        showHeader
        showModal={showMessageModal}
      >
        {showSpinner && (
          <>
            <p className="modal-message">{progressMessage}</p>
            <Spinner display="relative" hasMsg={false} noMargin />
          </>
        )}
      </WowModal>
      {showLoginModule && (
        <VipLoginModal
          ariaLabelledBy=""
          backdrop
          centered
          setShowModal={setShowLoginModule}
          showHeader
          showModal
          size="md"
          title={VIP_PAYWALL.paywallLoginTitle}
          titleImage={VIP_LOGO}
        >
          <LoginDetails
            setEnablePaymentModule={setEnablePaymentModule}
            setShowModal={setShowLoginModule}
          />
        </VipLoginModal>
      )}
      <style global jsx>{`
        @media (min-width: ${theme.breakpoints.lgUp}) {
          .paywall-header {
            display: flex;
            font-size: 42px;
            color: white;
            font-weight: bold;
            padding-left: 8%;
            text-align: center;
            padding-top: 20px;
            margin-bottom: -2rem;
          }
          .paywall-content-subtitle {
            font-weight: bold;
            font-size: 20px;
            color: white;
            display: flex;
            text-align: center;
            margin-left: 12%;
          }
          .paywall-content-vip-image {
            margin-top: -20px;
            transform: rotate(-20deg);
          }
          .paywall-content-divider-text {
            text-align: center;
            font-size: 24px;
            padding-top: 5px;
          }
          .paywall-content-table {
            margin-left: 100px;
            margin-right: 100px;
          }
        }
        @media (max-width: ${theme.breakpoints.lgDown}) {
          .paywall-header {
            display: flex;
            font-size: 32px;
            color: white;
            font-weight: bold;
            text-align: center;
            padding-top: 20px;
            margin-bottom: -2rem;
          }
          .paywall-content-subtitle {
            font-weight: bold;
            font-size: 20px;
            color: white;
            display: flex;
            text-align: center;
            padding-left: 5%;
            padding-right: 5%;
          }
          .paywall-content-vip-image {
            margin-top: -10px;
            transform: rotate(-20deg);
          }
          .paywall-content-divider-text {
            text-align: center;
            font-size: 18px;
            padding-top: 5px;
          }
          .paywall-content-table {
            margin-left: 100px;
            margin-right: 100px;
          }
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          .paywall-header {
            display: flex;
            font-size: 24px;
            color: white;
            font-weight: bold;
            width: 100%;
            padding-left: 5%;
            padding-right: 5%;
            flex-wrap: wrap;
            padding-bottom: 0px;
            padding-top: 20px;
            margin-bottom: -2rem;
          }
          .paywall-content-subtitle {
            font-weight: bold;
            padding-top: 0px;
            margin-top: -10px;
            font-size: 16px;
            color: white;
            display: flex;
            flex-wrap: wrap;
            text-align: center;
            padding-left: 5%;
            padding-right: 5%;
          }
          .paywall-content-vip-image {
            margin-top: -10px;
            transform: rotate(-20deg);
          }
          .paywall-content-divider-text {
            font-size: 14px;
            font-weight: 600;
          }
          .paywall-content-table {
            margin-top: 5px;
            padding-left: 10px;
            padding-right: 10px;
            margin-left: 0px;
            margin-right: 0px;
          }
        }
        .paywall-content-divider-text {
          font-weight: 700;
        }
        .paywall-content {
          margin-top: -20px;
        }
        .link-button {
          background: none;
          border: none;
          color: white;
          text-decoration: underline;
          cursor: pointer;
        }
        .paywall-header-wow {
          color: ${theme.colors.primary};
          padding-left: inherit;
          display: inline-block;
          background-repeat: no-repeat;
          background-size: contain;
          background-position: center bottom;
          padding-bottom: 5px;
          background-image: url(https://resources.wowcher.co.uk/assets/img/brand/wowcher/Wowcher+Squiggle.svg);
        }
        .paywall-content-subtitle-higlighted {
          color: ${theme.colors.primary};
          padding-left: inherit;
        }
        .paywall-content-divider {
          background-color: white;
          display: flex;
          margin-top: 15px;
        }
        .paywall-content-underline-text {
          padding-top: 10px;
          text-align: center;
        }
        .paywall-content-cancel-text {
          padding-top: 10px;
          color: white;
          text-align: center;
        }
        .paywall-content-savings-button {
          padding-top: 10px;
          text-align: center;
        }

        @media (max-width: ${theme.breakpoints.mdUp}) {
          .fullscreen-mobile .modal-dialog {
            width: 100%;
            min-width: 100%;
            min-height: 100%;
            height: 100%;
            top: 0;
            max-width: 100%;
            max-height: 100%;
            position: absolute;
            margin: 0;
          }
        }
        .modal-message {
          color: ${showSpinner
            ? theme.commoncolors.black
            : theme.commoncolors.successcolor};
          font-size: ${showSpinner ? '1.8rem' : '3rem'};
          text-align: center;
        }

        // animation move
        @keyframes mymove {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .with-animation .modal-content {
          animation: mymove 0.8s ease-out forwards;
        }

        // animation fade
        @keyframes fade-in {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .with-fade-in .modal-content {
          animation: fade-in 0.2s;
        }

        // animation slide in
        @keyframes slide-in {
          0% {
            transform: translateX(-100vw);
          }
          100% {
            transform: translateX(0vw);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes slide-in {
            0% {
              transform: none;
            }
            100% {
              transform: none;
            }
          }
        }

        @keyframes slide-out {
          100% {
            transform: translateX(-100vw);
          }
          0% {
            transform: translateX(0vw);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes slide-out {
            100% {
              transform: none;
            }
            0% {
              transform: none;
            }
          }
        }

        .with-slide-in.fade .modal-dialog {
          animation: slide-out 0.3s;
        }
        .with-slide-in.show .modal-dialog {
          animation: slide-in 0.3s;
        }

        // burger menu
        @media (max-width: ${theme.breakpoints.mdUp}) {
          .slide-out-menu .modal-dialog {
            width: calc(100vw - (100px));
            height: calc(100vh - (${topPosition + 52}px));
            bottom: 0;
            max-width: 100%;
            max-height: 100%;
            position: absolute;
            margin: 0;
          }
          .is-logged-in.slide-out-menu .modal-dialog {
            // height: calc(100vh - (97px));
          }
          .slide-out-menu .modal-content {
            height: 100%;
            width: 100%;
          }
          .slide-out-menu .modal-body {
            background-color: ${theme.colors.navbackground};
          }
        }

        // headerlogin-menu
        .headerlogin-menu {
          position: absolute;
          top: 10px;
          max-width: 180px;
          height: fit-content;
          -webkit-transform: translateX(5%);
          -ms-transform: translateX(5%);
          transform: translateX(5%);
        }
        .headerlogin-menu .modal-content {
          border: none;
          max-width: 130px;
          text-align: left;
          z-index: 500;

          -webkit-transform: translateX(30%);
          -ms-transform: translateX(30%);
          transform: translateX(30%);
        }
        .headerlogin-menu .modal-body {
          padding: 12px;
        }

        // subscribe confirmation
        .with-header-background button span {
          font-size: 14px;
          color: white;
          top: 0;
          position: relative;
          display: flex;
          font-weight: 100;
        }
        .with-header-background .modal-header {
          background-color: ${theme.colors.searchtext};
          border-top-left-radius: 3px;
          border-top-right-radius: 3px;
          color: #fff;
        }
        .with-header-background .modal-header .modal-title {
          font-weight: 200;
        }
        .with-header-background .modal-content {
          width: 100%;
          height: 100%;
        }
        .with-header-background .modal-body {
          padding: 0;
        }

        // subscribepopup
        .subscribepopup .modal-content {
          left: 80%;
          margin-top: 40px;
          max-width: 400px;
          transform: translateX(-75%);
          z-index: 500;
        }
        @media (min-width: ${theme.breakpoints.lgUp}) {
          .subscribepopup .modal-content {
            transform: translateX(-50%);
          }
        }
        @media (min-width: ${theme.breakpoints.xlUp}) {
          .subscribepopup .modal-content {
            transform: translateX(-25%);
          }
        }
        .modal-title-image {
          margin-left: 200px;
          padding: 10px 0 20px 0;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          .modal-title-image {
            margin-left: 130px;
            padding: 10px 0 20px 0;
          }
        }

        // booking calendar on redemption message
        .bcor-message {
          left: 10px;
          position: absolute;
          width: 95%;
        }
        .bcor-message .modal-body {
          padding-top: 0;
        }
      `}</style>
    </>
  );
};

export default VipPaywall;

VipPaywall.propTypes = {
  ariaLabelledBy: PropTypes.string.isRequired,
  backdrop: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  backdropClassName: PropTypes.string,
  container: PropTypes.object, // a DOM node
  dialogId: PropTypes.string,
  extraClasses: PropTypes.string,
  keyboard: PropTypes.bool,
  showModal: PropTypes.bool,
  size: PropTypes.string,
  topPosition: PropTypes.number,
};

VipPaywall.defaultProps = {
  backdrop: true,
  backdropClassName: '',
  dialogId: MODALS.default,
  extraClasses: '',
  size: '',
};
